// ============================================================================
// TENANT USER HOOKS
// ============================================================================
// React hooks for Tenant User Management API operations

import { useState, useEffect, useCallback } from 'react';
import { tenantUserService } from 'api/services';
import type {
  TenantUserResponse,
  CreateTenantUserRequest,
  UpdateTenantUserRequest,
  SetPasswordRequest,
  ID,
} from 'api/types';
import type { TenantHubPagedRequest, TenantHubPagedResult } from 'api/types/tenants.types';

/** Pagination metadata returned alongside the items */
export interface PaginationMeta {
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Hook to fetch tenant users with server-side pagination.
 * Re-fetches when tenantId or params change.
 */
export const useTenantUsers = (tenantId: string, params?: TenantHubPagedRequest) => {
  const [data, setData] = useState<TenantUserResponse[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>({
    totalCount: 0, pageNumber: 1, pageSize: 25,
    totalPages: 0, hasNextPage: false, hasPreviousPage: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!tenantId) {
      setData([]);
      setPagination(p => ({ ...p, totalCount: 0, totalPages: 0 }));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await tenantUserService.getAll(tenantId, params);
      const paged: TenantHubPagedResult<TenantUserResponse> | undefined = response.data;
      setData(paged?.items || []);
      if (paged) {
        setPagination({
          totalCount: paged.totalCount,
          pageNumber: paged.pageNumber,
          pageSize: paged.pageSize,
          totalPages: paged.totalPages,
          hasNextPage: paged.hasNextPage,
          hasPreviousPage: paged.hasPreviousPage,
        });
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [tenantId, params]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    data,
    pagination,
    loading,
    error,
    refetch: fetchUsers,
  };
};

/**
 * Hook to fetch a single tenant user by ID
 */
export const useTenantUser = (tenantId: string, userId: ID) => {
  const [data, setData] = useState<TenantUserResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUser = useCallback(async () => {
    if (!tenantId || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await tenantUserService.getById(tenantId, userId);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [tenantId, userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return {
    data,
    loading,
    error,
    refetch: fetchUser,
  };
};

/**
 * Hook to create a new tenant user
 */
export const useCreateTenantUser = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createTenantUser = async (
    tenantId: string,
    data: CreateTenantUserRequest,
    options?: {
      onSuccess?: (user: TenantUserResponse) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await tenantUserService.create(tenantId, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createTenantUser,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update a tenant user
 */
export const useUpdateTenantUser = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateTenantUser = async (
    tenantId: string,
    userId: ID,
    data: UpdateTenantUserRequest,
    options?: {
      onSuccess?: (user: TenantUserResponse) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await tenantUserService.update(tenantId, userId, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateTenantUser,
    loading,
    error,
    success,
  };
};

/**
 * Hook to delete a tenant user
 */
export const useDeleteTenantUser = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteTenantUser = async (
    tenantId: string,
    userId: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await tenantUserService.delete(tenantId, userId);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteTenantUser,
    loading,
    error,
    success,
  };
};

/**
 * Hook to reset a tenant user's password (admin action)
 */
export const useResetTenantUserPassword = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const resetPassword = async (
    tenantId: string,
    userId: ID,
    data: SetPasswordRequest,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await tenantUserService.resetPassword(tenantId, userId, data);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    resetPassword,
    loading,
    error,
    success,
  };
};
